import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  AiOutlineMenu,
  AiOutlineClose,
  AiOutlineShoppingCart,
} from "react-icons/ai";
import { VscDashboard, VscSignOut } from "react-icons/vsc";
import { FiLogIn, FiUserPlus } from "react-icons/fi";
import { BsChevronDown, BsChevronUp } from "react-icons/bs";

import { NavbarLinks } from "../../../data/navbar-links";
import { logout } from "../../../services/operations/authAPI";
import { ACCOUNT_TYPE } from "../../../utils/constants";
import logo from "../../../assets/Logo/kodevedic-logo-a-learning-platform-digital-learning-lms-platform.png";

export default function MobileSidebarMenu({ subLinks }) {
  const [open, setOpen]             = useState(false);
  const [catalogOpen, setCatalogOpen] = useState(false);

  const location  = useLocation();
  const navigate  = useNavigate();
  const dispatch  = useDispatch();
  const { token }      = useSelector((s) => s.auth);
  const { user }       = useSelector((s) => s.profile);
  const { totalItems } = useSelector((s) => s.cart);

  // Close on route change
  useEffect(() => { setOpen(false); }, [location.pathname]);

  // Lock body scroll while open
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  const isActive = (path) => location.pathname === path;

  const linkCls = (path) =>
    `flex items-center gap-3 rounded-xl px-4 py-3 text-sm font-medium transition-colors duration-150 ${
      isActive(path)
        ? "bg-yellow-50/10 text-yellow-50 border border-yellow-50/20"
        : "text-richblack-100 hover:bg-richblack-700 hover:text-richblack-5"
    }`;

  const handleLogout = () => {
    dispatch(logout(navigate));
    setOpen(false);
  };

  // ── Drawer + backdrop rendered via portal ─────────────────────────────────
  // Portals escape the navbar's CSS stacking context (created by backdrop-filter),
  // so fixed positioning and z-index work relative to the viewport root.
  const overlay = (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 z-[9998] bg-black/70 transition-opacity duration-300 ${
          open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
        onClick={() => setOpen(false)}
      />

      {/* Slide-in drawer */}
      <div
        className={`fixed top-0 right-0 z-[9999] flex h-screen w-[300px] max-w-[85vw] flex-col bg-richblack-800 border-l border-richblack-700 shadow-[-8px_0_40px_rgba(0,0,8,0.6)] transition-transform duration-300 ease-out ${
          open ? "translate-x-0" : "translate-x-full"
        }`}
      >
        {/* ── Drawer header ─── */}
        <div className="flex flex-shrink-0 items-center justify-between border-b border-richblack-700 px-5 py-4">
          <Link to="/" onClick={() => setOpen(false)}>
            <img src={logo} alt="KodeVedic" className="h-8 w-auto object-contain" />
          </Link>
          <button
            onClick={() => setOpen(false)}
            aria-label="Close menu"
            className="flex items-center justify-center rounded-lg p-1.5 text-richblack-300 hover:bg-richblack-700 hover:text-richblack-5 transition-colors duration-150"
          >
            <AiOutlineClose className="text-[20px]" />
          </button>
        </div>

        {/* ── Nav links ─── */}
        <nav className="flex-1 overflow-y-auto px-4 py-4 space-y-1">
          {NavbarLinks.map((link) =>
            link.title === "Catalog" ? (
              <div key="catalog">
                <button
                  onClick={() => setCatalogOpen((v) => !v)}
                  className={`flex w-full items-center justify-between rounded-xl px-4 py-3 text-sm font-medium transition-colors duration-150 ${
                    location.pathname.startsWith("/catalog")
                      ? "bg-yellow-50/10 text-yellow-50 border border-yellow-50/20"
                      : "text-richblack-100 hover:bg-richblack-700 hover:text-richblack-5"
                  }`}
                >
                  <span className="flex items-center gap-3">
                    {link.icon && <link.icon className="text-base flex-shrink-0" />}
                    {link.title}
                  </span>
                  {catalogOpen ? (
                    <BsChevronUp className="text-xs" />
                  ) : (
                    <BsChevronDown className="text-xs" />
                  )}
                </button>

                {catalogOpen && (
                  <div className="mt-1 ml-4 space-y-0.5 border-l border-richblack-700 pl-3">
                    {subLinks?.filter((s) => s?.courses?.length > 0).length ? (
                      subLinks
                        .filter((s) => s?.courses?.length > 0)
                        .map((sub) => (
                          <Link
                            key={sub._id}
                            to={`/catalog/${sub.name.split(" ").join("-").toLowerCase()}`}
                            onClick={() => setOpen(false)}
                            className={`block rounded-lg px-3 py-2 text-sm transition-colors duration-150 ${
                              location.pathname ===
                              `/catalog/${sub.name.split(" ").join("-").toLowerCase()}`
                                ? "text-yellow-50"
                                : "text-richblack-300 hover:text-richblack-5"
                            }`}
                          >
                            {sub.name}
                          </Link>
                        ))
                    ) : (
                      <p className="px-3 py-2 text-xs text-richblack-500">No courses found</p>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <Link key={link.title} to={link.path} className={linkCls(link.path)}>
                {link.icon && <link.icon className="text-base flex-shrink-0" />}
                {link.title}
              </Link>
            )
          )}
        </nav>

        {/* ── Divider ─── */}
        <div className="mx-4 flex-shrink-0 h-px bg-richblack-700" />

        {/* ── Auth + cart ─── */}
        <div className="flex-shrink-0 px-4 py-4 space-y-1.5">
          {user?.accountType === ACCOUNT_TYPE.STUDENT && (
            <Link to="/dashboard/cart" className={linkCls("/dashboard/cart")}>
              <AiOutlineShoppingCart className="text-base flex-shrink-0" />
              <span>Cart</span>
              {totalItems > 0 && (
                <span className="ml-auto flex h-5 w-5 items-center justify-center rounded-full bg-yellow-50 text-[9px] font-bold text-richblack-900">
                  {totalItems > 9 ? "9+" : totalItems}
                </span>
              )}
            </Link>
          )}

          {token ? (
            <>
              <Link to="/dashboard/my-profile" className={linkCls("/dashboard/my-profile")}>
                <VscDashboard className="text-base flex-shrink-0" />
                Dashboard
              </Link>
              <button
                onClick={handleLogout}
                className="flex w-full items-center gap-3 rounded-xl px-4 py-3 text-sm font-medium text-richblack-100 hover:bg-richblack-700 hover:text-richblack-5 transition-colors duration-150"
              >
                <VscSignOut className="text-base flex-shrink-0" />
                Log out
              </button>
            </>
          ) : (
            <>
              <Link to="/login" className={linkCls("/login")}>
                <FiLogIn className="text-base flex-shrink-0" />
                Log in
              </Link>
              <Link
                to="/signup"
                className="flex items-center gap-3 rounded-xl bg-yellow-50 px-4 py-3 text-sm font-bold text-richblack-900 hover:bg-yellow-25 transition-colors duration-150"
              >
                <FiUserPlus className="text-base flex-shrink-0" />
                Sign up
              </Link>
            </>
          )}
        </div>

        {/* ── Logged-in user strip ─── */}
        {token && user && (
          <>
            <div className="mx-4 flex-shrink-0 h-px bg-richblack-700" />
            <div className="flex flex-shrink-0 items-center gap-3 px-5 py-4">
              <img
                src={
                  user.image ||
                  `https://api.dicebear.com/5.x/initials/svg?seed=${user.firstName}`
                }
                alt="avatar"
                className="h-9 w-9 flex-shrink-0 rounded-full object-cover ring-2 ring-richblack-600"
              />
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold text-richblack-5">
                  {user.firstName} {user.lastName}
                </p>
                <p className="truncate text-xs text-richblack-400">{user.email}</p>
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );

  return (
    <div className="md:hidden">
      {/* Hamburger button - stays inside the navbar */}
      <button
        onClick={() => setOpen(true)}
        aria-label="Open menu"
        className="flex items-center justify-center p-2 text-richblack-100 hover:text-richblack-5 transition-colors duration-200"
      >
        <AiOutlineMenu className="text-[22px]" />
      </button>

      {/* Drawer + backdrop rendered at document.body via portal */}
      {createPortal(overlay, document.body)}
    </div>
  );
}
